import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save } from 'lucide-react';
import { auth } from '../utils/auth';
import { useLanguage } from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProfile = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [profile, setProfile] = useState({ username: '', email: '', password: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchProfile = useCallback(async () => {
    const token = auth.getToken();
    try {
      const response = await axios.get(`${API}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile({ username: response.data.username, email: response.data.email || '', password: '' });
    } catch {
      toast.error(t('toastProfileLoadError'));
      navigate('/admin/dashboard');
    } finally {
      setLoading(false);
    }
  }, [t, navigate]);

  useEffect(() => {
    const token = auth.getToken();
    if (!token) { navigate('/admin/login'); return; }
    fetchProfile();
  }, [fetchProfile, navigate]);

  const handleSave = async (e) => {
    e.preventDefault();
    const token = auth.getToken();
    try {
      setSaving(true);
      const updateData = {};
      if (profile.username) updateData.username = profile.username;
      if (profile.email) updateData.email = profile.email;
      if (profile.password) updateData.password = profile.password;

      const response = await axios.patch(`${API}/auth/profile`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update localStorage
      sessionStorage.setItem('admin_user', JSON.stringify(response.data));
      setProfile(prev => ({ ...prev, password: '' }));
      toast.success(t('toastProfileUpdated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toastSaveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="admin-container"><p>Laden...</p></div>;

  const currentUser = auth.getUser() || {};

  return (
    <div className="admin-container" data-testid="admin-profile-page">
      <div className="admin-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Link to="/admin/dashboard">
            <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <ArrowLeft size={18} /> Zurück
            </button>
          </Link>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1B5E20' }}>Mein Profil</h1>
        </div>
      </div>

      <div className="admin-content">
        <div className="admin-card" style={{ maxWidth: '600px' }}>
          <div style={{ marginBottom: '1.5rem', padding: '0.75rem 1rem', background: currentUser.role === 'admin' ? '#E8F5E9' : '#E3F2FD', borderRadius: '8px', fontSize: '0.875rem' }}>
            Rolle: <strong>{currentUser.role === 'admin' ? 'Admin' : 'Editor'}</strong>
          </div>

          <form onSubmit={handleSave}>
            <div className="form-group">
              <label className="form-label">Benutzername</label>
              <input
                type="text"
                value={profile.username}
                onChange={(e) => setProfile({ ...profile, username: e.target.value })}
                className="form-input"
                required
                data-testid="profile-username"
              />
            </div>
            <div className="form-group">
              <label className="form-label">E-Mail</label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                className="form-input"
                required
                data-testid="profile-email"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Neues Passwort <span style={{ color: '#999', fontWeight: 'normal' }}>(leer = unverändert)</span></label>
              <input
                type="password"
                value={profile.password}
                onChange={(e) => setProfile({ ...profile, password: e.target.value })}
                className="form-input"
                placeholder="Nur ausfüllen um Passwort zu ändern"
                data-testid="profile-password"
              />
            </div>
            <button type="submit" className="btn-primary" disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} data-testid="profile-save-btn">
              <Save size={18} /> {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;
